import React, { useState, useLayoutEffect, useEffect, useRef } from "react"

const buildImgServiceParams = (
  imageSize,
  focus = null,
  fixedSize = null,
  fitIn = null,
  fillColor = "ffffff",
  lowResPlaceholder = false
) => {
  let filters = []
  let size = ""
  let sizePrefix = ""
  let sizeSuffix = ""

  if (focus) {
    filters.push(`focal(${focus})`)
  } else {
    sizeSuffix = "/smart"
  }

  if (fitIn) {
    sizePrefix = "/fit-in"
    filters.push(`fill(${fillColor})`)
  }

  // fixedSize has prio over imageSize
  if (fixedSize) {
    size = fixedSize
  } else {
    // imageSize
    size = imageSize
  }

  if (lowResPlaceholder) {
    filters.push(`quality(10)`)
    filters.push(`blur(40)`)
  }

  return `/m${sizePrefix}/${size}${sizeSuffix}${
    filters.length > 0 ? "/filters:" + filters.join(":") : ""
  }`
}

const StoryblokImage = ({
  image,
  className,
  style,
  fixedSize,
  aspectRatio,
  fitIn,
  title,
  alt,
  forceLoad,
  fillColor,
  adaptToPixelRatio = false,
}) => {
  const [containerHeight, setContainerHeight] = useState(null)
  const [containerWidth, setContainerWidth] = useState(null)
  const [imageSize, setImageSize] = useState(null)
  const [preparing, setPreparing] = useState(true)
  const containerRef = useRef(null)
  const imageRef = useRef(null)
  const aspectClassname = aspectRatio
    ? `aspect-[${aspectRatio.split("by")[0]}/${aspectRatio.split("by")[1]}]`
    : ""
  let imageSrc = ""

  const getImageSize = () => {
    let dpiFactor = 1
    if (adaptToPixelRatio && window && window.devicePixelRatio) {
      dpiFactor = window.devicePixelRatio
    }
    const width = containerRef.current.clientWidth
    const height = containerRef.current.clientHeight
    const localContainerWidth = Math.ceil((dpiFactor * width) / 50) * 50
    let localContainerHeight = 0
    if (aspectRatio && aspectRatio !== "") {
      const [ratioWidth, ratioHeight] = aspectRatio.split("by")
      const h = (localContainerWidth / ratioWidth) * ratioHeight
      localContainerHeight = (dpiFactor * h).toFixed(0)
      setImageSize(`${localContainerWidth}x${localContainerHeight}`)
      setContainerWidth(localContainerWidth)
      setContainerHeight(localContainerHeight)
      setPreparing(false)
    } else {
      setContainerWidth(localContainerWidth)
      localContainerHeight = (dpiFactor * height).toFixed(0)
      setContainerHeight(localContainerHeight)
      setImageSize(`${localContainerWidth}x${localContainerHeight}`)
      setPreparing(false)
    }
  }

  useLayoutEffect(() => {
    if (containerRef.current) {
      getImageSize()
    }
  }, [containerRef])

  useEffect(() => {
    if (imageRef.current && forceLoad && !preparing) {
      const finalImg = new Image()
      finalImg.fetchpriority = "high"
      finalImg.src =
        image.filename +
        buildImgServiceParams(
          imageSize,
          image.focus,
          fixedSize,
          fitIn,
          fillColor
        )
      finalImg.onload = () => {
        imageRef.current.src = finalImg.src
      }
    }
  }, [imageRef, preparing, forceLoad])

  if (!image || !image.filename) {
    return <></>
  }

  if (!preparing) {
    imageSrc =
      image.filename +
      buildImgServiceParams(
        imageSize,
        image.focus,
        fixedSize,
        fitIn,
        fillColor,
        forceLoad
      )
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${className} ${aspectClassname}`}
    >
      {!preparing ? (
        <img
          loading={forceLoad ? "eager" : "lazy"}
          src={imageSrc}
          alt={alt}
          width={containerWidth}
          height={containerHeight}
          className={`object-contain ${className ? className : ""}`}
          title={title}
          style={style}
          ref={imageRef}
          fetchpriority={forceLoad ? "high" : "low"}
        />
      ) : (
        <></>
      )}
    </div>
  )
}

export default StoryblokImage
